import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Context } from 'Context/Context';
import useIsMobile from 'Hooks/useIsMobile';
import Header from 'Components/Header/Header';
import {
  caseDetailsProfApi, casesStatusUpdateApi, raiseNotificationApi, getDocumentDownloadUrlApi,
  noConflictInterestedToViewApi, noConflictNotInterestedToViewApi, reportCaseApi,
} from 'Constants';
import { getCaseType, getNetWorth, processReqReviewCount } from 'Utils';
import ConflictModal from 'Modals/ConflictModal';
import CloseCaseModal from 'Modals/CloseCaseModal';
import ReportModal from 'Modals/ReportModal';
import { Svg, CaseBtn, CaseListLayout, EmptyState } from './CaseListUI';
import { estimateOf, difficultyWord, suitabilityWord, place, yesNo, publicCo, Blur, StatusPill } from './CaseCard';
import {
  CASE_TYPES, SECTION_LINK, placeOf, typeLabel, writeCaseType, StageTracker, AnswerGrid, clientAnswers, fmtDate, clientNameOf,
} from './CaseStage';
import { sectionLabel } from './CaseListUI';
import MakeDownloadblePdf from './MakeDownloadblePdf';
import TeamUp from './TeamUp';
import TeamupInvitesReceived from './TeamupInvitesReceived';
import { TEAMUP_ENABLED } from 'Constants';

// ---------------------------------------------------------------------------
// Full case page (Case List handoff §7b) - /attorney/caseDetails/:id
// Opened from Approved cases, My cases, Not interested, Pending closing and
// Closed cases. Keeps the header + left case-type panel; breadcrumb back to
// the list; header card with the 5-step tracker; tabs Overview · Client &
// contact · Documents · AI insights · Activity. The stage actions (conflict
// check, request to close, …) are the same calls the old case popup made.
// ---------------------------------------------------------------------------

const TABS = ['Overview', 'Client & contact', 'Documents', 'AI insights', 'Activity'];
const OPEN_STATUSES = ['noConflict', 'reqToClose', 'closed'];      // documents + case-type answers unlocked
const CONTACT_STATUSES = ['approved', 'noConflict', 'reqToClose', 'closed'];

function Panel({ title, children, accent, right, style, className = '' }) {
  const isMobile = useIsMobile();
  return (
    <div className={`bg-white border ${accent ? 'border-brass/[.4]' : 'border-navy/[.12]'} rounded-md ${isMobile ? 'p-[18px]' : 'py-5 px-[22px]'} flex flex-col gap-3.5 min-w-0 ${className}`} style={style}>
      {(title || right) &&
        <div className="flex items-center justify-between gap-2.5">
          <span className="font-plexsans font-semibold text-[15px] leading-[1.2] text-navy">{title}</span>
          {right}
        </div>}
      {children}
    </div>
  );
}
const Muted = ({ children }) => <span className="font-plexsans font-normal text-[13.5px] leading-[1.5] text-navy/[.7]">{children}</span>;

function Fact({ label, value, urgent }) {
  return (
    <div className="flex flex-col gap-[5px] min-w-0">
      <span className="font-plexsans font-normal text-[12.5px] leading-none text-navy/[.55]">{label}</span>
      <span className={`font-plexsans font-semibold text-[14.5px] leading-[1.3] ${urgent ? 'text-danger' : 'text-navy'} [overflow-wrap:anywhere] normal-case`}>{value || '—'}</span>
    </div>
  );
}

function CasePage({ caseId }) {
  const { userDetails, makeApiCallFunc, reqRevCountArr, setReqRevCountArr, setSelectedMenuItem, setOffset } = useContext(Context);
  const userInfo = userDetails;
  const premium = !!userInfo?.isActiveSubscription;
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const snapshot = location.state?.snapshot || {};
  const cameFrom = location.state?.fromList || null;

  const [uc, setUc] = useState(null);          // attorney's UserCase row + caseDetails
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showConflict, setShowConflict] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [teamUpObj, setTeamUpObj] = useState(null);
  const [confirmedTeamUp, setConfirmedTeamUp] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMenu, setReportMenu] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [zipping, setZipping] = useState(false);
  const [dlFile, setDlFile] = useState(null);
  const loadSeq = useRef(0);

  const d = uc?.caseDetails || null;
  const status = uc?.caseStatus;
  const here = placeOf(uc || {});
  const section = here.section;
  const unlocked = OPEN_STATUSES.includes(status);
  const contactOpen = CONTACT_STATUSES.includes(status);
  const someoneReqToClose = !!reqRevCountArr?.[caseId]?.reqToClose;

  // ---------- load ----------
  const load = () => {
    const mine = ++loadSeq.current;
    makeApiCallFunc(
      caseDetailsProfApi,
      { caseId, userId: userInfo?._id, isNotActiveUser: !userInfo?.isActiveSubscription },
      ({ data }) => {
        if (mine !== loadSeq.current) return;
        if (data?.errorType) { setError(data.errorType); return; }
        const row = Array.isArray(data) ? data[0] : null;
        if (!row) { setError('deleted'); return; }
        setUc(row);
      },
      (err) => { console.log(err); if (mine === loadSeq.current) setError('failed'); },
    );
    makeApiCallFunc(
      caseDetailsProfApi,
      { caseId, userId: userInfo?._id, loadReqRev: true, isNotActiveUser: !userInfo?.isActiveSubscription },
      ({ data }) => {
        const rev = {};
        (Array.isArray(data) ? data : []).forEach((x) => { rev[x._id] = x.count; });
        setReqRevCountArr({ [caseId]: rev });
      },
      (err) => console.log(err),
    );
  };
  useEffect(() => { window.scrollTo(0, 0); load(); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (uc) setSelectedMenuItem(section); }, [section, uc]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- navigation ----------
  const goList = (sec, key) => {
    writeCaseType(sec, key);
    setSelectedMenuItem(sec);
    // straight back to the list the user came from (keeps their scroll), else open the list fresh
    if (cameFrom && cameFrom.section === sec && cameFrom.typeKey === key && window.history.length > 1) {
      navigate(-1);
    } else {
      setOffset(0);
      navigate(SECTION_LINK[sec] || SECTION_LINK['2']);
    }
  };
  const back = () => goList(section, here.typeKey);

  // ---------- helpers for the stage actions (same calls + notifications as the old case popup) ----------
  const notify = (obj, done) => makeApiCallFunc(raiseNotificationApi, obj, () => done && done(), (e) => console.log(e));
  const fullName = `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim();
  const caseLabel = d ? `${getCaseType(d.caseType)} - ${d.caseNumber}` : '';

  const noConflict = () => {
    setShowConflict(false); setBusy(true);
    makeApiCallFunc(casesStatusUpdateApi,
      { caseId: d._id, userId: userInfo._id, assistantId: null, caseStatus: 'noConflict' },
      () => {
        notify({
          title: 'Congratulations, Your case is being viewed by ' + fullName,
          is_read: false, sender: 'professional', type: '3', receiver: d.owner, caseNumber: d.caseNumber, notificationCategory: 1,
          additionalInfo: { caseId: d._id, userId: userDetails._id },
          content: userDetails?.role === 5
            ? 'The Law firm case manager ' + fullName + ' has indicated that they do not have any conflicts with regard to potentially representing you in this ' + getCaseType(d.caseType) + '-' + d.caseNumber + '.  The law firm will now view your case details.'
            : userInfo?.isUserFunder
              ? 'The litigation funder ' + userDetails?.company + ' has indicated that they do not have any conflicts with regard to potentially funding the representation of your ' + getCaseType(d.caseType) + '-' + d.caseNumber + '.  The litigation funder will now view your case details.'
              : 'The attorney ' + fullName + ' has indicated that they do not have any conflicts with regard to potentially representing you in this ' + getCaseType(d.caseType) + '-' + d.caseNumber + '.  The attorney will now view your case details.',
        });
        setBusy(false);
        toast.success('Conflict cleared. The client\'s contact details and documents are now unlocked.');
        load();
        // show the newly unlocked details right away
        setTab(1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      (e) => { setBusy(false); console.log(e); });
  };

  const onConflict = () => {
    setShowConflict(false); setBusy(true);
    makeApiCallFunc(casesStatusUpdateApi,
      { userId: userInfo?._id, caseId: d._id, assistantId: null, caseStatus: 'conflict' },
      () => {
        const userType = userInfo?.isUserFunder ? 'Litigation funder' : 'Attorney';
        notify({
          title: userType + ' removed from case ' + getCaseType(d.caseType) + ' ' + d.caseNumber,
          is_read: false, sender: 'professional', notificationCategory: 1, caseNumber: d.caseNumber, type: '4', receiver: d.owner,
          additionalInfo: { caseId: d._id, userId: userInfo._id },
          content: 'Unfortunately, the ' + userType + ' ' + userInfo?.firstName + ' ' + userInfo?.lastName + ' is no longer interested in this case. You may continue to seek other ' + userType + 's available for your case.',
        });
        setBusy(false);
        toast.info(d.caseNumber + ' removed from your list.');
        goList('2', 0);
      },
      (e) => { setBusy(false); console.log(e); });
  };

  const requestToClose = () => {
    setShowClose(false); setBusy(true);
    makeApiCallFunc(casesStatusUpdateApi,
      {
        caseId: uc.caseId, userId: userInfo._id, assistantId: null, caseStatus: 'reqToClose',
        teamup: { fromUserId: teamUpObj?.fromUserId, toUserId: teamUpObj?.toUserId },
      },
      ({ data }) => {
        if (data?.userCaseStatusCount) setReqRevCountArr(processReqReviewCount(data.userCaseStatusCount, userInfo));
        notify({
          title: 'The Legal Attorney has requested to close your ' + getCaseType(d.caseType) + ' - ' + d.caseNumber + '.',
          is_read: false, sender: 'professional', notificationCategory: 1, caseNumber: d.caseNumber, type: '3', receiver: d.owner,
          additionalInfo: { caseId: uc.caseId, userId: userDetails?._id },
          content: 'The ' + getCaseType(d.caseType) + ' - ' + d.caseNumber + ' is requested to be closed by the legal Attorney ' + userInfo?.firstName + ' ' + userInfo?.lastName + '. Please approve only if you have signed the contract.',
        });
        setBusy(false);
        toast.success(d.caseNumber + ' moved to Pending closing. The client will confirm.');
        load();
      },
      (e) => { setBusy(false); console.log(e); });
  };

  const revertClose = () => {
    setBusy(true);
    makeApiCallFunc(casesStatusUpdateApi,
      { caseId: uc.caseId, userId: userInfo._id, assistantId: null, caseStatus: 'noConflict', sendResp: true },
      () => {
        notify({
          title: 'The Legal Attorney has reverted the request to close the case ' + caseLabel + '.',
          is_read: false, sender: 'professional', caseNumber: d.caseNumber, notificationCategory: 1, type: '4', receiver: d.owner,
          additionalInfo: { caseId: uc.caseId, userId: userDetails?._id },
          content: 'The ' + caseLabel + ' is open again because professional reverted the request to close the case.',
        });
        setBusy(false);
        toast.info(d.caseNumber + ' moved back to My cases.');
        load();
      },
      (e) => { setBusy(false); console.log(e); });
  };

  const setInterest = (interested) => {
    setBusy(true);
    makeApiCallFunc(interested ? noConflictInterestedToViewApi : noConflictNotInterestedToViewApi,
      { caseId: d._id, userId: userInfo?._id, assistantId: uc?.assistantId?.[0]?._id, type: status },
      () => {
        notify(interested ? {
          title: 'Professional ' + fullName + ' has availability to view your case ' + caseLabel,
          is_read: false, sender: 'professional', type: '3', notificationCategory: 1, receiver: d.owner,
          additionalInfo: { caseId: d._id, userId: userDetails._id },
          content: 'A verified Legal Professional ' + fullName + ' has marked your case ' + caseLabel + ' as interested.',
        } : {
          title: 'Legal Attorney ' + fullName + ' who cleared conflict has marked your case ' + caseLabel + ' as not interested',
          is_read: false, sender: 'professional', notificationCategory: 1, type: '3', receiver: d.owner,
          additionalInfo: { caseId: d._id, userId: userDetails._id },
          content: 'A verified Legal Attorney ' + fullName + ' has marked your case ' + caseLabel + ' as not interested. We will continue to notify you with more attorneys as and when they show interest',
        });
        setBusy(false);
        toast.info(d.caseNumber + (interested ? ' moved to My cases.' : ' moved to Not interested.'));
        load();
      },
      (e) => { setBusy(false); console.log(e); toast.error('Could not update this case. Please try again.'); });
  };

  const reportCase = () => {
    setBusy(true);
    makeApiCallFunc(reportCaseApi, { caseId: uc.caseId, userId: uc.userId, reason: reportReason },
      () => {
        const userType = userInfo?.isUserFunder ? 'Litigation funder' : 'Attorney';
        notify({
          title: userType + ' removed from case ' + getCaseType(d.caseType) + ' ' + d.caseNumber,
          is_read: false, sender: 'professional', notificationCategory: 1, caseNumber: d.caseNumber, type: '4', receiver: d.owner,
          additionalInfo: { caseId: d._id, userId: userInfo._id },
          content: 'Unfortunately, the ' + userType + ' ' + userInfo?.firstName + ' ' + userInfo?.lastName + ' is no longer interested in this case. You may continue to seek other ' + userType + 's available for your case.',
        });
        setBusy(false); setReportOpen(false);
        toast.info(d.caseNumber + ' reported and removed from your cases.');
        goList(section, here.typeKey);
      },
      (e) => { setBusy(false); console.log(e); });
  };

  // ---------- documents ----------
  const docUrl = (documentTypeName, fileName, loc) => new Promise((resolve) => {
    makeApiCallFunc(getDocumentDownloadUrlApi, { caseId: d._id, documentTypeName, fileName, location: loc },
      ({ data }) => resolve(data?.url || null), (e) => { console.log(e); resolve(null); });
  });
  const downloadOne = (documentTypeName, f) => {
    const tabRef = window.open('', '_blank');   // open now so pop-up blockers allow it
    setDlFile(f.location || f.fileName);
    docUrl(documentTypeName, f.fileName, f.location).then((url) => {
      setDlFile(null);
      if (url) {
        if (tabRef) { try { tabRef.opener = null; } catch (e) { /* ignore */ } tabRef.location.href = url; } else window.location.href = url;
      } else { if (tabRef) tabRef.close(); toast.error('Could not download ' + f.fileName); }
    });
  };
  const downloadAll = async () => {
    const files = [
      ...(d?.supportingDocuments || []).map((f) => ({ t: 'supportingDocuments', ...f })),
      ...(d?.videoDocument || []).map((f) => ({ t: 'videoDocument', ...f })),
    ];
    if (!files.length || zipping) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      await Promise.all(files.map(async (f) => {
        const url = await docUrl(f.t, f.fileName, f.location);
        if (!url) return;
        const blob = await (await fetch(url)).blob();
        zip.file(f.fileName, blob);
      }));
      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, getCaseType(d.caseType) + '.zip');
    } catch (e) {
      console.log(e);
      toast.error('Could not download the documents');
    }
    setZipping(false);
  };

  // ================= render =================
  const shell = (children) => (
    <>
      <Header caseList={true} />
      {/* the route renders inside Layout's 6-column grid - span all of it */}
      <div className="col-span-6 w-full min-w-0">
      <CaseListLayout
        sectionKey={section}
        railItems={CASE_TYPES[section] || []}
        activeKey={here.typeKey}
        onSelect={(k) => goList(section, k)}
        setUpdateComp={() => {}}
      >
        {children}
      </CaseListLayout>
      </div>
    </>
  );

  if (error) {
    const msg = {
      reported: ['You reported this case', 'Reported cases are removed from your lists.'],
      conflict: ['You marked a conflict on this case', 'Cases with a conflict are removed from your lists.'],
      deleted: ['This case is no longer available', 'The client or an admin removed it.'],
      closed: ['This case has been closed', 'Another attorney closed this case with the client.'],
      sos: ['This is an SOS case', 'SOS cases are available with a Premium membership.'],
      justPosted: ['This case was just posted', 'Cases from the last 30 days are available with a Premium membership.'],
    }[error] || ['We couldn\'t load this case', 'Please try again in a moment.'];
    return shell(<EmptyState title={msg[0]} body={msg[1]} linkText="Back to my cases" onLink={() => goList('2', 1)} />);
  }
  if (!uc || !d) {
    return shell(
      <div className="flex flex-col gap-[18px]">
        <div className="h-3.5 w-[240px] rounded bg-navy/[.08]" />
        <div className="h-[180px] rounded-md bg-navy/[.06]" />
        <div className="h-[260px] rounded-md bg-navy/[.05]" />
      </div>
    );
  }

  // ---------- derived ----------
  const estimate = estimateOf(d);
  const deadline = d.aiDate ? moment(d.aiDate) : null;
  const deadlineUrgent = deadline ? deadline.diff(moment(), 'months', true) < 6 : false;
  const statusDate = fmtDate(uc.updated_at || snapshot.updated_at);
  const postedDate = fmtDate(d.created_at || snapshot.posted_at);
  const requestDate = fmtDate(uc.created_at || snapshot.created_at);
  const isOwnClose = uc.userId === userInfo?._id;
  const locked = (text) => <span className="text-brass"><Blur>{text}</Blur></span>;

  const pillFor = () => {
    if (status === 'approved') return <StatusPill tone="green">Approved{statusDate ? ' · ' + statusDate : ''}</StatusPill>;
    if (status === 'noConflict') return uc.caseInterest === 1
      ? <StatusPill>Not interested</StatusPill>
      : <StatusPill tone="green">Conflict cleared{statusDate ? ' · ' + statusDate : ''}</StatusPill>;
    if (status === 'reqToClose') return <StatusPill tone="brass">Close requested{statusDate ? ' · ' + statusDate : ''}</StatusPill>;
    if (status === 'closed') return <StatusPill>Closed{statusDate ? ' · ' + statusDate : ''}</StatusPill>;
    if (status === 'requested') return <StatusPill tone="brass">Awaiting client approval</StatusPill>;
    return null;
  };

  const activity = [
    status === 'closed' && { t: 'Case closed', at: statusDate },
    status === 'reqToClose' && { t: 'You requested to close', at: statusDate },
    status === 'noConflict' && uc.caseInterest === 1 && { t: 'Marked not interested', at: statusDate },
    status === 'noConflict' && uc.caseInterest !== 1 && { t: 'Conflict cleared', at: statusDate },
    status === 'approved' && { t: 'Approved by client', at: statusDate },
    { t: 'Request sent', at: requestDate },
    { t: 'Case posted', at: postedDate },
  ].filter(Boolean);

  const closeMessage = () => {
    const from = uc.teamupFromUser; const to = uc.teamupToUser;
    if (from && to?._id) {
      const requester = uc.userId === to._id ? to : from;
      const partner = requester._id === to._id ? from : to;
      if (requester._id === userInfo?._id) {
        return `You requested to close this case with your ${requester.isUserFunder ? 'attorney' : 'litigation funding'} partner ${partner?.firstName || ''} ${partner?.lastName || ''}${partner?.company ? ' from ' + partner.company : ''}. The client needs to confirm.`;
      }
      const who = requester.isUserFunder ? requester.company : `${requester.firstName} ${requester.lastName}${requester.company ? ' from ' + requester.company : ''}`;
      return `Your ${requester.isUserFunder ? 'litigation funding' : 'attorney'} partner ${who} has requested to close. The client needs to confirm.`;
    }
    return 'You asked the client to confirm this case is closed. It moves to Closed cases once they do.';
  };

  // "Next step" box: one line of guidance + the main action for the stage
  const nextStep = () => {
    switch (status) {
      case 'approved':
        return <Panel title="Next step" accent>
          <Muted>Run a conflict check with your firm. Once it's clear, mark it here to unlock the client's contact details and documents.</Muted>
          <CaseBtn onClick={() => setShowConflict(true)} disabled={busy} style={{ alignSelf: 'flex-start', padding: '12px 18px' }}>Mark conflict cleared</CaseBtn>
        </Panel>;
      case 'noConflict':
        if (uc.caseInterest === 1) {
          return <Panel title="Next step" accent>
            <Muted>You marked this case as not interested. Changed your mind? Move it back to My cases.</Muted>
            <CaseBtn onClick={() => setInterest(true)} disabled={busy} style={{ alignSelf: 'flex-start', padding: '12px 18px' }}>Mark as interested</CaseBtn>
          </Panel>;
        }
        return <Panel title="Next step" accent>
          <Muted>Contact the client and sign the contract. Then request to close, so other attorneys stop requesting this case.</Muted>
          {someoneReqToClose
            ? <span className="font-plexsans font-medium text-[13px] leading-[1.45] text-danger">Another attorney has asked to close this case. The client needs to confirm first.</span>
            : <CaseBtn onClick={() => setShowClose(true)} disabled={busy} style={{ alignSelf: 'flex-start', padding: '12px 18px' }}>Request to close</CaseBtn>}
          <span onClick={() => !busy && setInterest(false)} className="font-plexsans font-medium text-[12.5px] leading-none text-navy/[.6] underline cursor-pointer self-start">Mark as not interested</span>
        </Panel>;
      case 'reqToClose':
        return <Panel title="Next step" accent>
          <Muted>{closeMessage()}</Muted>
          {isOwnClose && <CaseBtn variant="secondary" onClick={revertClose} disabled={busy} style={{ alignSelf: 'flex-start', padding: '12px 18px' }}>Move back to My cases</CaseBtn>}
        </Panel>;
      case 'closed':
        return <Panel title="Case closed" accent>
          <Muted>Congratulations on closing this case. It will be deleted from the platform in 7 days - download what you need.</Muted>
          <CaseBtn onClick={() => setTab(2)} style={{ alignSelf: 'flex-start', padding: '12px 18px' }}>Go to documents</CaseBtn>
        </Panel>;
      case 'requested':
        return <Panel title="Next step" accent>
          <Muted>Waiting for the client to approve your request. We'll notify you when they do.</Muted>
        </Panel>;
      default:
        return <Panel title="Next step" accent><Muted>You don't have access to this case's details yet.</Muted></Panel>;
    }
  };

  // ---------- header card ----------
  const divider = <span className="w-px h-[22px] bg-brass/[.35] flex-none" />;
  const headerCard = (
    <div className="bg-white border border-navy/[.12] rounded-md overflow-hidden">
      <div className={`flex justify-between ${isMobile ? 'flex-col items-start gap-2.5 py-4 px-[18px]' : 'flex-row items-center gap-5 py-[18px] px-[26px]'} bg-brass/[.08] border-b border-brass/[.18]`}>
        {isMobile ? (
          <div className="flex flex-col gap-1 w-full">
            <div className="flex justify-between items-center gap-2">
              <span className="font-plexsans font-semibold text-[12px] leading-none text-navy">{d.caseNumber}</span>
              {pillFor()}
            </div>
            <span className="font-plexsans font-semibold text-[18px] leading-[1.3] text-navy">{getCaseType(d.caseType)}</span>
            <span className="font-plexsans font-semibold text-[18px] leading-[1.3] text-navy">{premium ? (estimate || 'Estimate pending') : <>${locked('000,000')} – ${locked('000,000')}</>}</span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3.5 flex-wrap min-w-0">
              <span className="font-plexsans font-semibold text-[22px] leading-[1.3] text-navy">{d.caseNumber}</span>
              {divider}
              <span className="font-plexsans font-semibold text-[22px] leading-[1.3] text-navy">{getCaseType(d.caseType)}</span>
              {divider}
              <span className="font-plexsans font-semibold text-[22px] leading-[1.3] text-navy">{premium ? (estimate || <span className="text-navy/[.45] font-medium">Estimate pending</span>) : <>${locked('000,000')} – ${locked('000,000')}</>}</span>
            </div>
            <span className="flex-none">{pillFor()}</span>
          </>
        )}
      </div>
      <div className={isMobile ? 'pt-[18px] px-3 pb-4' : 'pt-[22px] px-[26px] pb-5'}>
        <StageTracker status={status} />
      </div>
      <div className={`hide-vertical-scrollbar flex ${isMobile ? 'gap-[22px] py-0 px-[18px]' : 'gap-7 py-0 px-[26px]'} border-t border-navy/[.1] overflow-x-auto`}>
        {TABS.map((label, i) => (
          <span key={label} onClick={() => setTab(i)} className={`font-plexsans ${tab === i ? 'font-semibold text-navy border-b-brass' : 'font-medium text-navy/[.55] border-b-transparent'} text-[14px] leading-none pt-4 px-0 pb-[13px] border-b-[3px] border-solid cursor-pointer whitespace-nowrap`}>{label}</span>
        ))}
      </div>
    </div>
  );

  // ---------- tabs ----------
  const tagCls = 'font-plexsans font-medium text-[13px] leading-none bg-navy/[.05] border border-navy/[.12] rounded-[5px] py-2 px-3 whitespace-nowrap';
  const tags = [
    `Class action: ${yesNo(d.isClassAction)}`,
    `Multiple plaintiffs: ${yesNo(d.defendant)}`,
    `Adverse party net-worth: ${d.adversePartyNetWorth ? getNetWorth(d.adversePartyNetWorth) || 'Not available' : 'Not available'}`,
    d.isEntity === 'yes' ? 'Entity' : 'Individual',
    `Against public company: ${publicCo(d.adversePartyPublicCompany)}`,
  ];
  // [text colour, pill colours] per suitability band (was pill(color, bg, border))
  const suitTone = (v) => (v > 7 ? ['text-success', 'text-success bg-success/[.08] border-success/[.35]'] : v <= 4 ? ['text-danger', 'text-danger bg-danger/[.07] border-danger/[.3]'] : ['text-navy', 'text-navy bg-navy/[.05] border-navy/[.15]']);
  // pill() from CaseCard with the fontSize 12px / padding 7px 12px overrides applied
  const pillCls = 'inline-flex items-center gap-1.5 font-plexsans font-semibold text-[12px] leading-none border rounded-[20px] py-[7px] px-3 whitespace-nowrap';
  const ratings = (
    <div className="flex flex-wrap gap-2">
      {d.aiDifficulty ? <span className={`${pillCls} text-navy bg-navy/[.05] border-navy/[.15]`}>Difficulty: {d.aiDifficulty}/10 ({difficultyWord(d.aiDifficulty)})</span> : null}
      {TEAMUP_ENABLED && d.aiSuitability ? (premium
        ? <span className={`${pillCls} ${suitTone(d.aiSuitability)[1]}`}>Funder suitability: {d.aiSuitability}/10 ({suitabilityWord(d.aiSuitability)})</span>
        : <span className={`${pillCls} text-brass bg-brass/[.08] border-brass/[.3]`}>Funder suitability: <Blur>00</Blur>/10</span>) : null}
      {!d.aiDifficulty && !(TEAMUP_ENABLED && d.aiSuitability) && <Muted>No ratings yet.</Muted>}
    </div>
  );
  const activityList = (
    <div className="flex flex-col gap-3">
      {activity.map((a) => (
        <div key={a.t} className="flex justify-between gap-3 font-plexsans font-normal text-[13.5px] leading-[1.3] text-navy">
          <span>{a.t}</span><span className="text-navy/[.55]">{a.at || ''}</span>
        </div>
      ))}
    </div>
  );
  const adverse = contactOpen && d.adversePartyName ? d.adversePartyName : null;

  const overview = (
    <div className={`grid ${isMobile ? 'grid-cols-[minmax(0,1fr)]' : 'grid-cols-[minmax(0,2fr)_minmax(0,1fr)]'} gap-5 items-start`}>
      <div className={`flex flex-col gap-5 min-w-0 ${isMobile ? 'order-2' : 'order-1'}`}>
        <Panel title="Case details">
          <div className={`grid ${isMobile ? 'grid-cols-[minmax(0,1fr)]' : 'grid-cols-2'} gap-y-[18px] gap-x-7`}>
            <Fact label="Date of incident" value={d.dateInfo ? moment(d.dateInfo).format('MM/DD/YY') : ''} />
            <Fact label="Deadline to file lawsuit" value={premium ? (deadline ? deadline.format('MMMM DD, YYYY') : 'N/A') : locked('September 09, 2028')} urgent={premium && deadlineUrgent} />
            <Fact label="Location of incident" value={<span className="capitalize">{place(d.caseCity, d.caseState, d.caseZipcode)}</span>} />
            <Fact label="Location of client" value={<span className="capitalize">{place(d.clientCity, d.clientState, d.clientZipcode)}</span>} />
            {adverse && <Fact label="Adverse party" value={adverse} />}
          </div>
          <div className="flex flex-wrap gap-2">
            {premium ? tags.map((t) => <span key={t} className={`${tagCls} text-navy`}>{t}</span>)
              : <span className={`${tagCls} text-brass`}>Case tags come with Premium</span>}
          </div>
        </Panel>
        <Panel title="Client's answers">
          <AnswerGrid rows={clientAnswers(d, premium)} cols={1} />
          {!unlocked && <span className="font-plexsans font-normal text-[12.5px] leading-[1.45] text-navy/[.55]">The case-specific questions and the client's notes unlock once you clear the conflict check.</span>}
        </Panel>
        {TEAMUP_ENABLED && status === 'noConflict' && uc.caseInterest !== 1 && premium && !userInfo?.isUserFunder &&
          <Panel title="Team up with a funder">
            <TeamUp caseDetails={d} userInfo={userInfo} setConfirmedTeamUp={setConfirmedTeamUp} setUpdateComp={() => {}} setRefreshMainList={() => {}} isCaseReqToClose={someoneReqToClose} />
          </Panel>}
        {TEAMUP_ENABLED && userInfo?.isUserFunder && status !== 'reqToClose' && status !== 'closed' &&
          <Panel title="Team up requests">
            <TeamupInvitesReceived caseDetails={d} userInfo={userInfo} caseStatus={status} setConfirmedTeamUp={setConfirmedTeamUp} />
          </Panel>}
      </div>
      <div className={`flex flex-col gap-5 min-w-0 ${isMobile ? 'order-1' : 'order-2'}`}>
        {nextStep()}
        <Panel title="Ratings">{ratings}</Panel>
        <Panel title="Activity">{activityList}</Panel>
        {status !== 'closed' &&
          <div className="flex flex-col gap-2.5 py-0 px-1">
            <span onClick={() => setReportMenu((o) => !o)} className="font-plexsans font-medium text-[12.5px] leading-none text-danger underline cursor-pointer self-start">Report this case</span>
            {reportMenu &&
              <div className="flex flex-col bg-white border border-navy/[.12] rounded-md p-1">
                {['I think this case may be spam', 'This person is trying to sell me something', 'I think this may be a bot/robot'].map((r) => (
                  <span key={r} onClick={() => { setReportReason(r); setReportOpen(true); setReportMenu(false); }} className="font-plexsans font-normal text-[13px] leading-[1.35] text-navy py-[9px] px-2.5 rounded cursor-pointer hover:bg-[rgba(20,33,61,0.04)]">{r}</span>
                ))}
              </div>}
          </div>}
      </div>
    </div>
  );

  const contactRows = contactOpen ? [
    { q: 'Client name', a: d.isOwnerAnonymous && !clientNameOf(d) ? 'Anonymous' : (clientNameOf(d) || 'Not provided') },
    { q: 'Phone', a: d.contactPhone ? <a href={'tel:' + d.contactPhone} className="text-navy underline">{d.contactPhone}</a> : 'Not provided' },
    { q: 'Email', a: d.contactEmail ? <a href={'mailto:' + d.contactEmail} className="text-navy underline">{d.contactEmail}</a> : 'Not provided' },
    ...(d.contactPref ? [{ q: 'Preferred contact', a: d.contactPref }] : []),
    { q: 'Currently represented by an attorney?', a: d.attAvailable ? (d.attAvailable === 'yes' ? 'Yes' : 'No') : 'Not provided' },
    { q: 'Client location', a: <span className="capitalize">{place(d.clientCity, d.clientState, d.clientZipcode) || '—'}</span> },
  ] : [];
  const contact = (
    <div className="max-w-[720px]">
      <Panel title="Client & contact">
        {contactOpen ? <AnswerGrid rows={contactRows} cols={1} /> : <Muted>The client's contact details unlock once the client approves your request.</Muted>}
        {status === 'approved' && <Muted>Please clear the conflict check before contacting the client.</Muted>}
      </Panel>
    </div>
  );

  const docRows = (name, title) => {
    const list = d?.[name] || [];
    return (
      <div className="flex flex-col gap-2">
        <span className="font-plexsans font-semibold text-[12px] leading-none tracking-[.06em] uppercase text-navy/[.55]">{title}</span>
        {list.length ? list.map((f, i) => (
          <div key={(f.location || f.fileName) + i} className="flex items-center justify-between gap-3 py-[11px] px-0 border-b border-navy/[.08]">
            <span className="font-plexsans font-medium text-[13.5px] leading-[1.35] text-navy min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{f.fileName}</span>
            <span onClick={() => (dlFile ? null : downloadOne(name, f))} className={`flex-none font-plexsans font-semibold text-[12.5px] leading-none text-brass ${dlFile ? 'cursor-default' : 'cursor-pointer'} underline`}>
              {dlFile === (f.location || f.fileName) ? 'Opening…' : 'Download'}
            </span>
          </div>
        )) : <Muted>{name === 'videoDocument' ? 'No video statement was uploaded.' : 'No documents were uploaded.'}</Muted>}
      </div>
    );
  };
  const hasDocs = (d?.supportingDocuments?.length || 0) + (d?.videoDocument?.length || 0) > 0;
  const documents = (
    <div className="max-w-[720px]">
      <Panel title="Documents" right={unlocked && hasDocs ? <CaseBtn variant="secondary" onClick={downloadAll} disabled={zipping} style={{ padding: '9px 14px', fontSize: '12.5px' }}>{zipping ? 'Preparing…' : 'Download all (.zip)'}</CaseBtn> : null}>
        {unlocked ? <>
          {docRows('supportingDocuments', 'Supporting documents')}
          {docRows('videoDocument', 'Video statement')}
          <div className="flex items-center gap-2.5 pt-1">
            <MakeDownloadblePdf caseDetails={d} content={<span className="inline-flex items-center gap-[7px] font-plexsans font-semibold text-[13px] leading-none text-navy bg-white border border-navy/[.25] rounded-md py-[11px] px-4 cursor-pointer"><Svg d={['M12 3v12', 'M7 10l5 5 5-5', 'M4 20h16']} size={15} width={2} />Download case details (PDF)</span>} />
          </div>
        </> : <Muted>Documents unlock once you clear the conflict check.</Muted>}
      </Panel>
    </div>
  );

  const aiInsights = (
    <div className={`grid ${isMobile ? 'grid-cols-[minmax(0,1fr)]' : 'grid-cols-2'} gap-5 max-w-[900px]`}>
      <Panel title="Case estimate">
        <span className="font-plexsans font-semibold text-[22px] leading-[1.3] text-navy">{premium ? (estimate || 'Estimate pending') : <>${locked('000,000')} – ${locked('000,000')}</>}</span>
        <Muted>AI estimate of the settlement range, from the details the client gave.</Muted>
      </Panel>
      <Panel title="Deadline to file">
        <span className={`font-plexsans font-semibold text-[22px] leading-[1.3] ${premium && deadlineUrgent ? 'text-danger' : 'text-navy'}`}>{premium ? (deadline ? deadline.format('MMMM DD, YYYY') : 'N/A') : locked('September 09, 2028')}</span>
        <Muted>{premium && deadlineUrgent ? 'Less than 6 months away.' : 'AI estimate of the statute-of-limitations deadline.'}</Muted>
      </Panel>
      <Panel title="Difficulty">
        <span className="font-plexsans font-bold text-[22px] leading-none text-navy">{d.aiDifficulty ? <>{d.aiDifficulty}<span className="text-[14px] font-medium text-navy/[.45]">/10 · {difficultyWord(d.aiDifficulty)}</span></> : 'N/A'}</span>
        <Muted>How complex this case may be to litigate.</Muted>
      </Panel>
      {TEAMUP_ENABLED && <Panel title="Litigation funder suitability">
        <span className={`font-plexsans font-bold text-[22px] leading-none ${premium && d.aiSuitability ? suitTone(d.aiSuitability)[0] : 'text-navy'}`}>{d.aiSuitability ? (premium ? <>{d.aiSuitability}<span className="text-[14px] font-medium text-navy/[.45]">/10 · {suitabilityWord(d.aiSuitability)}</span></> : locked('00/10')) : 'N/A'}</span>
        <Muted>How likely a litigation funder is to be interested in this case.</Muted>
      </Panel>}
    </div>
  );

  const activityTab = <div className="max-w-[720px]"><Panel title="Activity">{activityList}</Panel></div>;

  return shell(
    <div className="flex flex-col gap-[18px]">
      {showConflict && <ConflictModal uploadNew={showConflict} caseDetails={d} onClose={() => setShowConflict(false)} noConflict={noConflict} onConflict={onConflict} />}
      {showClose &&
        <CloseCaseModal
          uploadNew={showClose}
          userInfo={userInfo}
          caseDetails={d}
          confirmedTeamUp={confirmedTeamUp}
          setTeamUpObj={setTeamUpObj}
          teamUpObj={teamUpObj}
          onClose={() => { setShowClose(false); toast.info('Sign the contract with the client before you request to close.'); }}
          onSubmit={requestToClose}
        />}
      {reportOpen && <ReportModal uploadNew={reportOpen} onSubmit={reportCase} onClose={() => setReportOpen(false)} />}

      {/* breadcrumb */}
      <div className={`flex items-center gap-2 flex-wrap font-plexsans font-medium text-[13.5px] leading-[1.3] ${isMobile ? 'mt-3' : 'mt-0'}`}>
        <span onClick={back} className="flex items-center gap-1.5 text-brass cursor-pointer">
          <Svg d="M15 6l-6 6 6 6" size={14} width={2.2} />{sectionLabel(section, userInfo?.isUserFunder)} / {typeLabel(section, here.typeKey)}
        </span>
        <span className="text-navy/[.35]">/</span>
        <span className="text-navy font-semibold">{d.caseNumber}</span>
      </div>

      {headerCard}

      {tab === 0 && overview}
      {tab === 1 && contact}
      {tab === 2 && documents}
      {tab === 3 && aiInsights}
      {tab === 4 && activityTab}
    </div>
  );
}

export default CasePage;
